import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * ScrollToTop — resets the window scroll position to (0, 0) whenever the
 * route changes. Without this, React Router preserves the previous page's
 * scroll position, leading to the user landing mid-page on the new route.
 *
 * Behaviour:
 *   • If the URL has a hash (e.g. /life#sadaqah), we do nothing — the
 *     browser/anchor logic handles in-page navigation.
 *   • Otherwise, we scroll the window AND the documentElement to (0, 0)
 *     immediately (no smooth animation — the user expects to land at the
 *     top instantly when they click a nav link).
 *
 * Mount this component once, inside <BrowserRouter>, BEFORE the <Routes>.
 */
const ScrollToTop = () => {
  const { pathname, hash } = useLocation();

  useEffect(() => {
    if (hash) return; // let the in-page anchor handle it

    // Use 'auto' (instant) — most users find smooth scrolling on route
    // changes disorienting (the new page renders while still scrolling).
    try {
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    } catch {
      // Older browsers may not support the options-object signature
      window.scrollTo(0, 0);
    }
    // Belt-and-braces for browsers/themes that scroll the documentElement.
    if (document.documentElement) document.documentElement.scrollTop = 0;
    if (document.body) document.body.scrollTop = 0;
  }, [pathname, hash]);

  return null;
};

export default ScrollToTop;
